import { ref, remove, set } from "firebase/database";
import { db } from "../firebaseConfig";

const SEGMENT_DEFS_OPS_QUEUE_KEY = "trekkin_segments_defs_ops_queue_v1";
const SEGMENT_DEFS_OPS_EVENT = "trekkin:segments-defs-ops-queue-updated";

function isOnlineNow() {
  try {
    return typeof navigator === "undefined" ? true : navigator.onLine !== false;
  } catch {
    return true;
  }
}

function normalizeQueue(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      opId: String(item?.opId || "").trim(),
      type: String(item?.type || "").trim(),
      segmentId: String(item?.segmentId || "").trim(),
      payload:
        item?.payload && typeof item.payload === "object" ? { ...item.payload } : null,
      createdAt: Number(item?.createdAt || 0),
      updatedAt: Number(item?.updatedAt || 0),
    }))
    .filter((item) => item.opId && item.type && item.segmentId);
}

function readQueue() {
  try {
    const raw = localStorage.getItem(SEGMENT_DEFS_OPS_QUEUE_KEY);
    if (!raw) return [];
    return normalizeQueue(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  const normalized = normalizeQueue(queue);
  try {
    if (!normalized.length) localStorage.removeItem(SEGMENT_DEFS_OPS_QUEUE_KEY);
    else localStorage.setItem(SEGMENT_DEFS_OPS_QUEUE_KEY, JSON.stringify(normalized));
  } catch {}
  try {
    window.dispatchEvent(
      new CustomEvent(SEGMENT_DEFS_OPS_EVENT, {
        detail: { queue: normalized },
      })
    );
  } catch {}
  return normalized;
}

function makeOpId(prefix = "segdef-op") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function makeSegmentId() {
  return `seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toSignature(payload = {}) {
  const name = String(payload?.name || "").trim().toLowerCase();
  const a = `${Number(payload?.startLat || 0).toFixed(6)},${Number(payload?.startLon || 0).toFixed(6)}`;
  const b = `${Number(payload?.endLat || 0).toFixed(6)},${Number(payload?.endLon || 0).toFixed(6)}`;
  return `${name}|${a}|${b}`;
}

export function loadSegmentDefinitionOpsQueue() {
  return readQueue();
}

export function applySegmentDefinitionOps(baseDefs = {}, queue = readQueue()) {
  const map = { ...(baseDefs && typeof baseDefs === "object" ? baseDefs : {}) };
  normalizeQueue(queue).forEach((op) => {
    if (op.type === "create_or_replace" && op.payload) {
      map[op.segmentId] = { ...op.payload, id: op.segmentId, pendingSync: true };
      return;
    }
    if (op.type === "remove") {
      delete map[op.segmentId];
    }
  });
  return map;
}

export function enqueueCreateSegmentDefinition(payload = {}) {
  const queue = readQueue();
  const now = Date.now();
  const normalized = {
    ...(payload || {}),
    id: String(payload?.id || "").trim() || makeSegmentId(),
    updatedAt: now,
  };
  const sig = toSignature(normalized);
  const existing = queue.find(
    (item) =>
      item?.type === "create_or_replace" &&
      item?.payload &&
      toSignature(item.payload) === sig
  );
  if (existing?.segmentId) {
    existing.payload = { ...(existing.payload || {}), ...normalized, id: existing.segmentId };
    existing.updatedAt = now;
    writeQueue(queue);
    return { segmentId: existing.segmentId, queue };
  }
  const segmentId = String(normalized.id || makeSegmentId());
  queue.push({
    opId: makeOpId("segdef-create"),
    type: "create_or_replace",
    segmentId,
    payload: { ...normalized, id: segmentId },
    createdAt: now,
    updatedAt: now,
  });
  writeQueue(queue);
  return { segmentId, queue };
}

export function enqueueRemoveSegmentDefinition(segmentId) {
  const id = String(segmentId || "").trim();
  if (!id) return { removed: false };
  let queue = readQueue().filter((item) => item.segmentId !== id);
  queue.push({
    opId: makeOpId("segdef-remove"),
    type: "remove",
    segmentId: id,
    payload: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  writeQueue(queue);
  return { removed: true, queue };
}

export async function flushSegmentDefinitionOpsQueue() {
  if (!isOnlineNow()) {
    return { ok: false, reason: "offline", applied: 0, pending: readQueue().length };
  }
  let queue = readQueue();
  let applied = 0;
  while (queue.length) {
    const op = queue[0];
    try {
      if (op.type === "create_or_replace" && op.payload) {
        await set(ref(db, `segments/definitions/${op.segmentId}`), {
          ...(op.payload || {}),
          id: op.segmentId,
          pendingSync: null,
          updatedAt: Number(op?.payload?.updatedAt || Date.now()),
        });
      } else if (op.type === "remove") {
        await remove(ref(db, `segments/definitions/${op.segmentId}`));
      }
      queue = queue.slice(1);
      writeQueue(queue);
      applied += 1;
    } catch (err) {
      const msg = String(err?.message || err || "").toLowerCase();
      const offline =
        !isOnlineNow() ||
        msg.includes("network") ||
        msg.includes("offline") ||
        msg.includes("failed to fetch") ||
        msg.includes("unavailable") ||
        msg.includes("connection");
      return {
        ok: false,
        reason: offline ? "offline_during_flush" : "flush_error",
        applied,
        pending: queue.length,
        err: err?.message || String(err),
      };
    }
  }
  return { ok: true, applied, pending: 0 };
}

let autoFlushInitialized = false;
let autoFlushRunning = false;

export function initSegmentDefinitionOpsAutoFlush() {
  if (autoFlushInitialized || typeof window === "undefined") return;
  autoFlushInitialized = true;

  const run = async () => {
    if (autoFlushRunning) return;
    if (!isOnlineNow()) return;
    autoFlushRunning = true;
    try {
      await flushSegmentDefinitionOpsQueue();
    } catch {
      // no-op, retries on next signal
    } finally {
      autoFlushRunning = false;
    }
  };

  window.addEventListener("online", run);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
  window.addEventListener(SEGMENT_DEFS_OPS_EVENT, run);
  window.setTimeout(run, 1500);
}

export { SEGMENT_DEFS_OPS_EVENT, SEGMENT_DEFS_OPS_QUEUE_KEY };
